import { ChevronsUpDown } from "lucide-react";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useIntercom } from "react-use-intercom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";

import { getName } from "components/navigation/menu-bar";
import { stringAvatar } from "components/shared/shared";
import { TruncatedText } from "components/shared/truncated-text";
import {
  GetUserIfLoggedIn,
  User,
  getActiveOrganisation,
  useIdentity,
  useIdentityContext,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchUserMemberships } from "data/queries-common";
import * as Routes from "routes";

export function OrgSwitcher() {
  const { isMobile } = useSidebar();
  const [identityState] = useIdentity();
  const { switchOrg } = useIdentityContext();
  const activeOrg = getActiveOrganisation(identityState);
  const user: User | false = GetUserIfLoggedIn(identityState);
  const organisationList = user ? user.organisations : [];
  const history = useHistory();
  const location = useLocation<{ switchToOrganisationId?: string }>();
  const { update } = useIntercom();
  const identityName = getName(identityState);

  useEffect(() => {
    const organisationId = location.state?.switchToOrganisationId;
    if (location.pathname !== Routes.hub || !organisationId) return;
    const org = organisationList.find((organisation) => organisation.organisationId === organisationId);
    history.replace({ ...location, state: { ...location.state, switchToOrganisationId: undefined } });
    if (!org || org.hasAccess === false) return;
    update({
      company: { companyId: org.organisationId, name: org.name },
      customAttributes: { current_organisation: org.name },
    });
    switchOrg(org);
  }, [location, organisationList, history, switchOrg, update]);

  const membershipsQuery = useQuery({
    queryKey: [identityState, QueryKeys.userMembership],
    queryFn: () => fetchUserMemberships(true),
  });

  const getOrgSubscription = (orgId: string) => {
    return membershipsQuery.data?.find((o) => o.organisationId === orgId)
      ?.subscription;
  };

  const avatarData = identityName ? stringAvatar(identityName) : null;
  const subscription = activeOrg
    ? getOrgSubscription(activeOrg.organisationId) ?? activeOrg.subscription
    : undefined;

  if (!identityName) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="pointer-events-none">
            <Skeleton className="size-8 rounded-lg" />
            <div className="grid flex-1 gap-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (organisationList.length <= 1) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs">
              {avatarData?.children}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
              <TruncatedText as="span" className="font-medium max-w-full">
                {identityName}
              </TruncatedText>
              {subscription && (
                <TruncatedText as="span" className="text-xs max-w-full">
                  {subscription}
                </TruncatedText>
              )}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs">
                {avatarData?.children}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                <TruncatedText as="span" className="font-medium max-w-full">
                  {identityName}
                </TruncatedText>
                {subscription && (
                  <TruncatedText as="span" className="text-xs max-w-full">
                    {subscription}
                  </TruncatedText>
                )}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organisations
            </DropdownMenuLabel>
            {organisationList
              .filter((o) => o !== activeOrg)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => {
                const orgAvatarData = stringAvatar(org.name);
                const orgSub =
                  getOrgSubscription(org.organisationId) ?? org.subscription;
                const hasAccess = org.hasAccess !== false;

                return (
                  <DropdownMenuItem
                    key={org.organisationId}
                    onClick={() => {
                      if (!hasAccess) return;
                      // Switch only after navigation is allowed so keeping a draft also keeps its organisation.
                      history.push(Routes.hub, { switchToOrganisationId: org.organisationId });
                    }}
                    className="gap-2 p-2"
                    disabled={!hasAccess}
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border text-[10px]">
                      {orgAvatarData.children}
                    </div>
                    <div className="grid flex-1 leading-tight min-w-0">
                      <TruncatedText as="span" className="text-sm max-w-full">
                        {org.name}
                      </TruncatedText>
                      {orgSub && (
                        <TruncatedText as="span" className="text-xs max-w-full">
                          {orgSub}
                        </TruncatedText>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
